import { faArrowRight, faDotCircle, faDrawPolygon, faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/react";
import * as turf from "@turf/turf";
import { bearing, point } from "@turf/turf";
import { notification } from "antd";
import CssFilterConverter from "css-filter-converter";
import dayjs, { Dayjs } from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { Feature, FeatureCollection } from "geojson";
import mapboxgl from "mapbox-gl";
import {
    MapboxStyleDefinition,
    MapboxStyleSwitcherControl,
    MapboxStyleSwitcherOptions,
} from "mapbox-gl-style-switcher";
import React from "react";
import { Layer } from "react-map-gl/mapbox";
import { globalAppConfig } from "../../config";
import { GlobalStoreContextType } from "../../context/GlobalStoreContext";
import Helper from "../../Helper";
import mapIcons from "../../mapIcons";
import { Area } from "../../models/Area";
import { Delivery } from "../../models/Delivery";
import { Equipment } from "../../models/Equipment";
import { MapObject } from "../../models/MapObject";
import { MapObjectRadius } from "../../models/MapObjectRadius";
import PlotAPI from "../../plotAPI";

// dayjs.extend(isSameOrBefore);
// dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
// dayjs.extend(utc);

export function areaOpen(area: MapObject, date: Dayjs | null, mapAreas: Area[], equipment: Equipment[]) {
    if (!date) {
        return true;
    }

    const filterDate = date;

    const item =
        mapAreas.find((item) => item.id === area.mappableId) || equipment.find((item) => item.id === area.mappableId);

    if (!item) {
        return true;
    }

    // Open and close dates currently stored in backend using Ruby Date object that has no tz info.
    // When this is converted to a date object on the frontend it doesn't have a tz so it uses UTC then offsets it to the system time.
    // This causes issues in actual comparisons down the road so we're offsetting it manually back below.
    // BIG TODO: Fix this in the backend to store the dates in datetime so it doesn't require this wonkyness.

    // Area has an open date but no end date. i.e. Location doesn't open until this date.
    if (item.openDate) {
        const openDate = item.openDate.subtract(item.openDate.utcOffset(), "minute");

        if (filterDate.isBefore(openDate, "day")) {
            return false;
        }
    }
    // Area has a close date but open date is empty. i.e. area is closed until further notice after this date.
    if (item.closeDate) {
        const closeDate = item.closeDate.add(item.closeDate.utcOffset(), "minute");

        // If the filterDate is after the closeDate then that Area is not open.
        if (filterDate.isAfter(closeDate, "day") || filterDate.isSame(closeDate, "day")) {
            return false;
        }
    }

    // Exception Days

    if (
        item instanceof Area &&
        item.areaExceptionDays?.some((exception) => {
            // Exception day start and end provided. Today is in the window provided
            if (
                exception.exceptionStart &&
                exception.exceptionEnd &&
                filterDate.isBetween(exception.exceptionStart, exception.exceptionEnd, "day", "[]")
            ) {
                return true;
            }
            // Temp closure provided start date but no end date. i.e. Closed until further notice.
            if (
                (exception.exceptionStart &&
                    !exception.exceptionEnd &&
                    filterDate.isAfter(exception.exceptionStart, "day")) ||
                filterDate.isSame(exception.exceptionStart, "day")
            ) {
                return true;
            }
            // Temp closure provided end date but no start date. i.e. Closed until this date.
            if (
                !exception.exceptionStart &&
                exception.exceptionEnd &&
                filterDate.isBefore(exception.exceptionEnd, "day")
            ) {
                return true;
            }
        })
    ) {
        return false;
    }

    return true;
}

export function equipmentOpen(equipment: Equipment, date: Dayjs | null) {
    if (!date) {
        return true;
    }

    const filterDate = date;

    if (equipment.openDate) {
        const openDate = equipment.openDate.subtract(equipment.openDate.utcOffset(), "minute");

        if (filterDate.isBefore(openDate)) {
            return false;
        }
    }
    if (equipment.closeDate) {
        const closeDate = equipment.closeDate.add(equipment.closeDate.utcOffset(), "minute");

        // If the filterDate is after the closeDate then that Area is not open.
        if (filterDate.isAfter(closeDate) || filterDate.isSame(closeDate, "day")) {
            return false;
        }
    }

    if (
        equipment.equipmentTempClosures?.some((exception) => {
            // Exception day start and end provided. Today is in the window provided
            if (
                exception.closureStart &&
                exception.closureEnd &&
                filterDate.isBetween(exception.closureStart, exception.closureEnd, "day", "[]")
            ) {
                return true;
            }
            // Temp closure provided start date but no end date. i.e. Closed until further notice.
            if (
                (exception.closureStart &&
                    !exception.closureEnd &&
                    filterDate.isAfter(exception.closureStart, "day")) ||
                filterDate.isSame(exception.closureStart, "day")
            ) {
                return true;
            }
            // Temp closure provided end date but no start date. i.e. Closed until this date.
            if (!exception.closureStart && exception.closureEnd && filterDate.isBefore(exception.closureEnd, "day")) {
                return true;
            }
        })
    ) {
        return false;
    }

    return true;
}

export function addressFormater(projectInfo) {
    let addressString = "";
    const addressValues = [
        projectInfo.address_1,
        projectInfo.address_2,
        projectInfo.city,
        projectInfo.state,
        projectInfo.zip_code,
    ];

    addressValues.forEach((val) => {
        if (val) {
            addressString += val;
            if (val === (projectInfo.address_1 || projectInfo.address_2 || projectInfo.city)) {
                addressString += ", ";
            } else {
                addressString += " ";
            }
        }
    });

    return addressString;
}

export function iconType(objectType: string) {
    if (objectType === "point") {
        return faDotCircle;
    } else if (objectType === "polygon") {
        return faDrawPolygon;
    } else {
        return faArrowRight;
    }
}

export function areaAvatar(area: Area | Equipment, mapObject: MapObject, size: string = "1rem") {
    if (!mapObject) {
        return null;
    }
    if (mapObject.icon) {
        const style = {
            width: size,
            filter: mapObject.icon?.includes("sdf") ? CssFilterConverter.hexToFilter(mapObject.color).color : null,
        };
        return <img style={style} src={`/img/png/map/${mapObject.icon}.png`} />;
    }

    if (area instanceof Area && area.areaName === "Jobsite Entrance" && !mapObject.icon) {
        return <FontAwesomeIcon icon={faMapMarkerAlt} color={mapObject.color} width={size} />;
    } else {
        return (
            <FontAwesomeIcon
                icon={iconType(mapObject.geometry)}
                color={mapObject.geometry == "lineString" ? mapObject.outline : mapObject.color}
                width={size}
            />
        );
    }
}

export function equipmentAvatar(mapObject: MapObject, size: string = "1rem") {
    if (mapObject.icon) {
        const style = {
            width: size,
            filter: mapObject.icon?.includes("sdf") ? CssFilterConverter.hexToFilter(mapObject.color).color : null,
        };
        return <img style={style} src={`/img/png/map/${mapObject.icon}.png`} className="mr-2" />;
    }

    return (
        <FontAwesomeIcon
            icon={iconType(mapObject.geometry)}
            color={mapObject.geometry.toUpperCase() === "LINESTRING" ? mapObject.outline : mapObject.color}
            width={size}
            className="mr-2"
        />
    );
}

export function geocoderHelperResultFormatter(result) {
    result.context.push({
        id: result.id,
        text: result.text,
    });
    const location = {
        name: null,
        address: null,
        city: null,
        zip: null,
        state: null,
        country: null,
        lon: result.geometry.coordinates[0],
        lat: result.geometry.coordinates[1],
    };
    if (result.place_type.includes("address")) {
        location.address = result.place_name.substring(0, result.place_name.indexOf(","));
    }
    if (result.place_type.includes("poi")) {
        location.address = result.properties.address;
        location.name = result.place_name.substring(0, result.place_name.indexOf(","));
    }
    location.city = result.context.find((x) => {
        return x.id.startsWith("place");
    }).text;
    if (
        result.context.find((x) => {
            return x.id.startsWith("postcode");
        })
    ) {
        location.zip = result.context.find((x) => {
            return x.id.startsWith("postcode");
        }).text;
    }
    location.state = result.context.find((x) => {
        return x.id.startsWith("region");
    }).text;
    location.country = result.context.find((x) => {
        return x.id.startsWith("country");
    }).text;
    return location;
}

export function validateZipCode(val) {
    return /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(val);
}

export function findLayerIndex(array, val) {
    return array.findIndex((layer) => layer.features.find((feature) => feature.id === val));
}
export function findAreaIndex(area, val) {
    return area[this.findLayerIndex(area, val)].features.findIndex((area) => area.id === val);
}
export function findObjectById(id, layers) {
    const result = layers[this.findLayerIndex(layers, id)].features[this.findAreaIndex(layers, id)];
    return result;
}

export function breakTimesUpdate(break_times, currentObject, currentLayers, callBack) {
    const object = currentObject;
    object.properties.plot.area_break_times = break_times;
    const layers = currentLayers;
    layers[this.findLayerIndex(layers, object.id)].features[this.findAreaIndex(layers, object.id)] = object;
    callBack("map_layers", layers);
}
export function exceptionDaysUpdate(exception_days, currentObject, currentLayers, callBack) {
    const object = currentObject;
    object.properties.plot.area_exception_days = exception_days;
    const layers = currentLayers;
    layers[this.findLayerIndex(layers, object.id)].features[this.findAreaIndex(layers, object.id)] = object;
    callBack("map_layers", layers);
}

export function returnCoordinates(object: MapObject) {
    if (object.geometry === "point") {
        return object.coordinates;
    } else if (object.geometry === "lineString") {
        return object.coordinates[0];
    }

    return object.coordinates[0][0];
}

export function isMobileDevice() {
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        return true;
    } else {
        return false;
    }
}

const urlWaypointConstructor = (mapObject: MapObject) => {
    let wayPoints = "&waypoints=";

    if (mapObject.geometry === "lineString") {
        mapObject.coordinates.forEach((coord, index) => {
            wayPoints += `${coord[1]},${coord[0]}`;
            if (index < mapObject.coordinates.length - 1) {
                wayPoints += "|";
            }
        });
    } else {
        const coordinates = mapObject.getCenter();
        wayPoints += `${coordinates[1]},${coordinates[0]}`;
    }
    return wayPoints;
};

const waypointLineStringConstructor = (mapObject: MapObject) => {
    let wayPoints = "";
    mapObject.coordinates.forEach((coord) => {
        wayPoints += `${coord[1]},${coord[0]}|`;
    });
    return wayPoints;
};

export function openMapLink(mapObjects: MapObject[]) {
    if (!mapObjects || mapObjects.length === 0) return;

    let destination = "";
    let waypoints = "";

    // If it's only a single map object just use the destination.
    if (mapObjects.length === 1) {
        const obj = mapObjects[0];
        if (obj.geometry === "lineString") {
            obj.coordinates.forEach((coord, idx) => {
                const coordStr = `${coord[1]},${coord[0]}`;
                if (idx === obj.coordinates.length - 1) {
                    destination = coordStr;
                } else {
                    waypoints += coordStr + "|";
                }
            });
            waypoints = waypoints.replace(/\|$/, "");
        } else {
            const center = obj.getCenter();
            destination = `${center[1]},${center[0]}`;
        }
    } else {
        mapObjects.forEach((obj, idx) => {
            if (idx === mapObjects.length - 1) {
                const center = obj.getCenter();
                destination = `${center[1]},${center[0]}`;
            } else if (obj.geometry === "lineString") {
                waypoints += waypointLineStringConstructor(obj);
            } else {
                const center = obj.getCenter();
                waypoints += `${center[1]},${center[0]}|`;
            }
        });
        waypoints = waypoints.replace(/\|$/, "");
    }

    const url = new URL("https://www.google.com/maps/dir/?api=1&travelmode=driving&origin=My+Location");
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isIOS && destination) {
        url.protocol = "maps:";
        url.host = "maps.google.com";
    }
    if (destination?.length > 0) url.searchParams.append("destination", destination);
    if (waypoints?.length > 0) url.searchParams.append("waypoints", waypoints);
    window.open(url.toString());
}

export const getMapObjectCenter = (object: MapObject) => {
    const points = turf.points(object.coordinates);

    return turf.center(points);
};

export const getMapMaxMetersPerPixelAtZoom = (map: mapboxgl.Map) => {
    if (!map) {
        return 1; // Default to 1 meter per pixel if map is not available
    }
    const y = map.getCanvas()?.height;
    const x = map.getCanvas()?.width;

    const left = map.unproject([0, y]);
    const bottom = map.unproject([x, y]);
    const maxMeters =
        turf.distance(turf.point([left.lng, left.lat]), turf.point([bottom.lng, bottom.lat]), {
            units: "miles",
        }) * 1609.344;
    const metersPerPixel = maxMeters / x;
    return metersPerPixel;
};

export const getIconRealWorldSize = (mapObject: MapObject, metersPerPixel: number) => {
    let size = mapObject.iconWidth || 1;
    if (mapObject.iconUnits !== "meters") {
        size = size * 0.3048;
    }
    const iconPixels = size / metersPerPixel;
    const iconSize = iconPixels / 128; // 128 is the default size of the icon in pixels. All icons are 128x128
    if (iconSize && iconSize !== undefined && iconSize !== Infinity) {
        return iconSize;
    } else {
        return 1;
    }
};

export const defaultIconSizeArray = (iconType: boolean): any[] => {
    const svgIconSizeArray = ["interpolate", ["linear"], ["zoom"], 12, 0, 13, 0.1, 14, 0.5, 16, 1, 17, 0.5];
    const iconSizeArray = ["interpolate", ["linear"], ["zoom"], 12, 0, 13, 0.1, 14, 0.2, 16, 0.25];

    if (iconType) {
        return iconSizeArray;
    }
    return svgIconSizeArray;
};

export const mapStyles: MapboxStyleDefinition[] = [
    {
        title: "Satellite-ESRI",
        uri: globalAppConfig.ARC_GIS_IMAGERY_URL,
    },
    // Breaks when using react-map-gl
    // {
    //     title: "Satellite-Mapbox",
    //     uri: `mapbox://styles/mapbox/standard-satellite`,
    // },
    {
        title: "Streets",
        uri: "mapbox://styles/mapbox/streets-v12",
    },
    {
        title: "Satellite Streets",
        uri: "mapbox://styles/mapbox/satellite-streets-v12",
    },
    {
        title: "Dark",
        uri: "mapbox://styles/mapbox/dark-v11",
    },
    {
        title: "Light",
        uri: "mapbox://styles/mapbox/light-v11",
    },
    {
        title: "Outdoors",
        uri: "mapbox://styles/mapbox/outdoors-v12",
    },
];

export const addMapControls = (map: mapboxgl.Map, mapStyleOptions: MapboxStyleSwitcherOptions) => {
    if (!map) {
        return;
    }
    //top-left controls

    // top-right controls
    map.addControl(new mapboxgl.GeolocateControl(), "top-right");
    map.addControl(new mapboxgl.NavigationControl(), "top-right");
    map.addControl(new mapboxgl.FullscreenControl(), "top-right");
    map.addControl(new MapboxStyleSwitcherControl(mapStyles, mapStyleOptions), "top-right");

    // bottom-right controls
    map.addControl(new mapboxgl.ScaleControl(), "bottom-right");
};

// TODO: This is being added to try and help debug `addToBounds` below.
const extractLngLatPairs = (input: any): [number, number][] => {
    const result: [number, number][] = [];
    const invalidItems: any[] = [];

    const recurse = (item: any) => {
        if (Array.isArray(item) && item.length === 2 && typeof item[0] === "number" && typeof item[1] === "number") {
            result.push(item as [number, number]);
        } else if (Array.isArray(item)) {
            item.forEach(recurse);
        } else {
            invalidItems.push(item);
        }
    };

    recurse(input);

    if (invalidItems.length > 0) {
        Sentry.captureMessage("Non-lng/lat items encountered in extractLngLatPairs", {
            level: "warning",
            extra: {
                invalidItems,
                originalInput: input,
            },
        });
    }

    return result;
};

export const addToBounds = (object: MapObject, currentBounds: mapboxgl.LngLatBounds): mapboxgl.LngLatBounds => {
    try {
        const bounds = currentBounds || new mapboxgl.LngLatBounds();

        const lngLatPairs = extractLngLatPairs(object.coordinates);
        lngLatPairs.forEach((pair) => bounds.extend(pair));

        return bounds;
    } catch (error) {
        Sentry.captureException(error, {
            extra: {
                object,
                currentBounds,
            },
        });
        return new mapboxgl.LngLatBounds();
    }
};

export const generateMapGeoJson = (mapObjects: MapObject[]): FeatureCollection => {
    const geoJsonData: { type: string; features: any[] } = {
        type: "FeatureCollection",
        features: [],
    };

    mapObjects.map((mapObject) => {
        geoJsonData.features.push(mapObject.toFeature());
        if (mapObject.geometry == "lineString") {
            geoJsonData.features = geoJsonData.features.concat(arrowCapSourceBuilder(mapObject));
        }
        if (mapObject.radiuses && mapObject.radiuses.length > 0) {
            mapObject.radiuses.map((radius) => {
                geoJsonData.features.push(radius.toFeature(mapObject));
            });
        }
    });

    return geoJsonData as FeatureCollection;
};

export const addGeoJsonSourceToMap = (
    map: mapboxgl.Map,
    sourceId: string,
    mapObjects: MapObject[],
    context: GlobalStoreContextType
) => {
    if (map.getSource(sourceId)) {
        return;
    }
    const data = generateMapGeoJson(mapObjects);

    return map.addSource(sourceId, {
        type: "geojson",
        data: data,
        // Cannot use dynamic here because it causes issues with rendering polygons
        // dynamic: true,
    });
};

export const addIcon = (
    object: MapObject,
    sourceId: string,
    map: mapboxgl.Map,
    visibility: "visible" | "none",
    iconSize: number | any[]
) => {
    return (
        <>
            {object.radiuses && object.radiuses.length > 0 && (
                <React.Fragment>
                    {object.radiuses.map((radius) => {
                        return (
                            <React.Fragment key={`${object.uuid}_radius_${radius.id}`}>
                                <Layer
                                    key={`${object.uuid}_radius_${radius.id}`}
                                    id={`${object.uuid}_radius_${radius.id}`}
                                    source={sourceId}
                                    type="fill"
                                    paint={{
                                        "fill-opacity": radius.opacity || 1,
                                        "fill-color": radius.color || "#ff9e00",
                                    }}
                                    layout={{
                                        visibility: visibility,
                                    }}
                                    filter={["==", ["get", "id"], `${object.uuid}_radius_${radius.id}`]}
                                />

                                <Layer
                                    key={`${object.uuid}_radius_border_${radius.id}`}
                                    id={`${object.uuid}_radius_border_${radius.id}`}
                                    source={sourceId}
                                    type="line"
                                    layout={{
                                        visibility: radius.showBorder ? visibility : "none",
                                    }}
                                    paint={{
                                        "line-color": radius.color || "#ff9e00",
                                        "line-width": 2,
                                        "line-opacity": 1,
                                    }}
                                    filter={["==", ["get", "id"], `${object.uuid}_radius_${radius.id}`]}
                                />

                                {radius.name &&
                                    map &&
                                    map.isStyleLoaded() &&
                                    !map?.getStyle()?.sprite?.includes("arcgis") && (
                                        <Layer
                                            key={`${object.uuid}_radius_label_${radius.id}`}
                                            id={`${object.uuid}_radius_label_${radius.id}`}
                                            source={sourceId}
                                            type="symbol"
                                            layout={{
                                                visibility: visibility,
                                                "text-field": radius.name,
                                                "symbol-placement": "line",

                                                "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
                                                "text-size": 12,
                                                "text-anchor": "center",
                                                "text-allow-overlap": true,
                                            }}
                                            paint={{
                                                "text-color": "#FFFFFF",
                                                "text-halo-color": "#222222",
                                                "text-halo-width": 1,
                                            }}
                                            filter={["==", ["get", "id"], `${object.uuid}_radius_${radius.id}`]}
                                        />
                                    )}
                            </React.Fragment>
                        );
                    })}
                </React.Fragment>
            )}
            <Layer
                id={object.uuid}
                source={sourceId}
                type="symbol"
                layout={{
                    visibility: visibility,
                    "icon-image": object.icon || "bullet_sdf_icon",
                    "icon-size": iconSize,
                    "icon-anchor": object.iconAnchor ? object.mapboxIconAnchor() : "center",
                    "icon-allow-overlap": true,
                    "icon-ignore-placement": true,
                }}
                paint={{
                    "icon-color": object.color || "#ff9e00",
                    "icon-opacity": object.opacity || 1,
                    "icon-translate-transition": { duration: 2000 },
                    "icon-halo-width-transition": { duration: 2000 },
                }}
                filter={["==", ["get", "id"], object.uuid]}
            />
        </>
    );
};

export const addLine = (
    object: MapObject,
    sourceId: string,
    visibility: "visible" | "none",
    iconSize: number | any[]
) => {
    return (
        <React.Fragment key={object.uuid}>
            <Layer
                id={object.uuid}
                source={sourceId}
                type="line"
                paint={{
                    "line-color": object.outline || object.color || "#ff9e00",
                    "line-width": 5,
                    "line-opacity": object.opacity ? object.opacity : 1.0,
                }}
                layout={{
                    visibility: visibility,
                    "line-join": "round",
                    "line-cap": "round",
                }}
                filter={["==", ["get", "id"], object.uuid]}
            />
            {object.icon && (
                <React.Fragment>
                    <Layer
                        key={`${object.uuid}_line_icon`}
                        id={`${object.uuid}_line_icon`}
                        source={sourceId}
                        type="symbol"
                        layout={{
                            visibility: visibility,
                            "symbol-placement": "line",
                            "symbol-spacing": 1,
                            "icon-image": object.icon ? object.icon : "arrow_sdf_icon",
                            "icon-size": iconSize,
                            "icon-allow-overlap": true,
                            "icon-rotate": object.icon && object.icon.includes("no-entry") ? 90 : 0,
                        }}
                        paint={{
                            "icon-color": object.color || object.outline || "#ff9e00",
                        }}
                        filter={["==", ["get", "id"], object.uuid]}
                    />
                    <Layer
                        key={`${object.uuid}_lineCaps`}
                        id={`${object.uuid}_lineCaps`}
                        source={sourceId}
                        type="symbol"
                        layout={{
                            visibility: visibility,
                            "icon-image": object.icon || iconImage(object),
                            "icon-size": iconSize,
                            "icon-rotate": ["get", "rotation"],
                            "icon-anchor": "center",
                            "icon-rotation-alignment": "map",
                            "icon-allow-overlap": true,
                            "icon-ignore-placement": true,
                        }}
                        paint={{
                            "icon-color": object.color || object.outline || "#ff9e00",
                        }}
                        filter={[
                            "in",
                            ["get", "id"],
                            ["literal", [`${object.uuid}_startCap`, `${object.uuid}_endCap`]],
                        ]}
                    />
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

export const addPolygon = (
    object: MapObject,
    sourceId: string,
    visibility: "visible" | "none",
    iconSize: number | any[]
) => {
    return (
        <React.Fragment key={object.uuid}>
            <Layer
                key={object.uuid}
                id={object.uuid}
                source={sourceId}
                type="fill"
                paint={{
                    "fill-color": object.color || "#ff9e00",
                    "fill-opacity": object.opacity || 0.75,
                }}
                layout={{
                    visibility: visibility,
                }}
                filter={["==", ["get", "id"], object.uuid]}
            />

            <Layer
                key={`${object.uuid}_outline`}
                id={`${object.uuid}_outline`}
                source={sourceId}
                type="line"
                paint={{
                    "line-color": object.outline || object.color || "#ff9e00",
                    "line-width": 2,
                    "line-dasharray": [10],
                }}
                layout={{
                    visibility: visibility,
                }}
                filter={["==", ["get", "id"], object.uuid]}
            />
            {object.icon && (
                <Layer
                    key={`${object.uuid}_outline_icon`}
                    id={`${object.uuid}_outline_icon`}
                    source={sourceId}
                    type="symbol"
                    layout={{
                        visibility: visibility,
                        "symbol-placement": "line",
                        "symbol-spacing": 1,
                        "icon-image": object.icon ? object.icon : "arrow_sdf_icon",
                        "icon-size": iconSize,
                        // ["interpolate", ["linear"], ["zoom"], 10, 0.7, 15, 0.3],
                        "icon-allow-overlap": true,
                        "icon-rotate": object.icon && object.icon.includes("no-entry") ? 90 : 0,
                    }}
                    paint={{
                        "icon-color": object.outline || object.color || "#ff9e00",
                    }}
                    filter={["==", ["get", "id"], object.uuid]}
                />
            )}
        </React.Fragment>
    );
};

export const addPolygonIconBorder = async (
    map: mapboxgl.Map,
    object: MapObject,
    layerId: string,
    visibility: "visible" | "none",
    iconSize = 1
) => {
    return map.addLayer({
        id: `${object.uuid}_outline_icon`,
        type: "symbol",
        source: layerId,
        layout: {
            visibility: visibility,
            "symbol-placement": "line",
            "symbol-spacing": 1,
            "icon-image": object.icon ? object.icon : "arrow_sdf_icon",
            "icon-size": iconSize,
            // ["interpolate", ["linear"], ["zoom"], 10, 0.7, 15, 0.3],
            "icon-allow-overlap": true,
            "icon-rotate": object.icon && object.icon.includes("no-entry") ? 90 : 0,
        },
        paint: {
            "icon-color": object.outline || object.color || "#ff9e00",
        },

        filter: ["==", ["get", "id"], object.uuid],
    });
};

const arrowCapSourceBuilder = (object: MapObject): Feature[] => {
    return [
        {
            id: `${object.uuid}_startCap`,
            type: "Feature",
            properties: {
                id: `${object.uuid}_startCap`,
                rotation:
                    object.icon && object.icon.includes("no-entry")
                        ? getStartCapBearing(object) + 90 + 90
                        : getStartCapBearing(object) + 90,
            },
            geometry: {
                type: "Point",
                coordinates: object.coordinates[0],
            },
        },
        {
            id: `${object.uuid}_endCap`,
            type: "Feature",
            geometry: {
                type: "Point",
                coordinates: object.coordinates.at(-1),
            },
            properties: {
                id: `${object.uuid}_startCap`,
                rotation:
                    object.icon && object.icon.includes("no-entry")
                        ? getEndCapBearing(object) + 270 + 90
                        : getEndCapBearing(object) + 270,
            },
        },
    ];
};

const getEndCapBearing = (object: MapObject) => {
    if (object.coordinates?.length < 2) {
        return 0; // Not enough points to calculate bearing
    }
    const point1 = point(object.coordinates[object.coordinates.length - 2]);
    const point2 = point(object.coordinates[object.coordinates.length - 1]);
    const mapBearing = bearing(point1, point2);
    return mapBearing;
};
const getStartCapBearing = (object: MapObject) => {
    if (object.coordinates?.length < 2) {
        return 0; // Not enough points to calculate bearing
    }
    const point1 = point(object.coordinates[1]);
    const point2 = point(object.coordinates[0]);
    const mapBearing = bearing(point1, point2);
    return mapBearing;
};
const iconImage = (object: any) => {
    if (object.icon) {
        return object.icon;
    }
    return "bullet_sdf_icon";
};

export const addMarkerImages = async (map: mapboxgl.Map) => {
    const flatMap = [...mapIcons.equipmentIcons, ...mapIcons.sdfIcons];

    return Promise.all(
        flatMap
            .filter((icon) => icon !== undefined && icon !== "")
            .map((icon) => {
                map.loadImage(`/img/png/map/${icon}.png`, (error, image) => {
                    if (!image) {
                        return;
                    }
                    // if (error) throw error;
                    // Add the image to the map style.
                    if (!map.hasImage(icon)) {
                        map.addImage(icon, image, { sdf: icon.includes("sdf") ? true : false });
                    }
                });
            })
    );
};

export const toggleShowOpenLocations = (
    map: mapboxgl.Map,
    context: GlobalStoreContextType,
    mapObjects: MapObject[],
    date: Dayjs = dayjs()
) => {
    mapObjects.forEach((mapObject) => {
        const mappable = mapObject.getMappable(context);
        if (!mappable) {
            return;
        }

        let visibility: "visible" | "none" = "none";

        if (
            mappable instanceof Area &&
            areaOpen(
                mapObject,
                date,
                context.mapAreasForProject(context.currentProjectInfo.unique_token),
                context.equipmentForProject(context.currentProjectInfo.unique_token)
            )
        ) {
            visibility = "visible";
        } else if (mappable instanceof Equipment && equipmentOpen(mappable, date)) {
            visibility = "visible";
        }

        mapObject.setVisibility(map, visibility);
    });
};

export const deliveryMapToggleDisplayLogic = (
    mapRef: mapboxgl.Map,
    context: GlobalStoreContextType,
    date: Dayjs,
    mapObjects: MapObject[] = []
) => {
    if (!mapRef) {
        return;
    }
    const eventsForDay = context.deliveriesByProject
        .map((project) => project.deliveries)
        .flat()
        .filter((event) => (event?.startDateTime?.isSame(date, "day") ? true : false));

    mapObjects.forEach((mapObject) => {
        const mappable = mapObject.getMappable(context);
        if (!mappable) {
            return;
        }
        let visibility: "visible" | "none" = "none";
        if (calendarShowArea(context, mappable, eventsForDay)) {
            visibility = "visible";
        }
        mapObject.setVisibility(mapRef, visibility);
    });
};

export const calendarShowArea = (context, mappable: Area | Equipment, eventsForDay: Delivery[]) => {
    if (context.deliveriesFilterInfo?.dropOffAreaIds?.includes(mappable.id)) {
        return true;
    }

    if (context.deliveriesFilterInfo?.waypointAreaIds.includes(mappable.id)) {
        return true;
    }

    if (eventsForDay.some((event) => event.dropOffAreaId === mappable.id)) {
        return true;
    }
    if (eventsForDay.some((event) => event.waypointAreaId === mappable.id)) {
        return true;
    }

    if (
        eventsForDay.some((event) =>
            event.deliveriesEquipment?.some((equipment) => equipment.equipmentId === mappable.id)
        )
    ) {
        return true;
    }

    return false;
};
type mapWeatherEndpoints =
    | "temperature"
    | "dewPoint"
    | "humidity"
    | "windSpeed"
    | "windDirection"
    | "precipitationIntensity"
    | "visibility"
    | "lightning";
export const addRadar = (
    map: mapboxgl.Map,
    context: GlobalStoreContextType,
    endpoint: mapWeatherEndpoints = "precipitationIntensity"
) => {
    if (!map) {
        return;
    }

    const jobsiteEntrance = context
        .mapAreasForProject(context.currentProjectInfo.unique_token)
        .find((area) => area.areaName === "Jobsite Entrance");

    map.setZoom(map.getZoom() > 6 ? 6 : map.getZoom());
    if (map.getSource("tomorrow-io-api")) {
        map.removeSource("tomorrow-io-api").addSource("tomorrow-io-api", {
            type: "raster",
            tiles: [`${Helper.baseUrlWithPort()}/tomorrow_proxy/v4/map/tile/{z}/{x}/{y}/${endpoint}/now.png`],
            tileSize: 256,
            attribution: '&copy; <a href="https://www.tomorrow.io/weather-api">Powered by Tomorrow.io</a>',
        });
    } else {
        map.addSource("tomorrow-io-api", {
            type: "raster",
            tiles: [`${Helper.baseUrlWithPort()}/tomorrow_proxy/v4/map/tile/{z}/{x}/{y}/${endpoint}/now.png`],
            tileSize: 256,
            attribution: '&copy; <a href="https://www.tomorrow.io/weather-api">Powered by Tomorrow.io</a>',
        });
    }
    if (map.getLayer("radar-tiles")) {
        map.removeLayer("radar-tiles");
    }

    map.addLayer({
        id: "radar-tiles",
        type: "raster",
        source: "tomorrow-io-api",
        minzoom: 1,
    }).moveLayer(jobsiteEntrance?.getMapObject(context)?.uuid || "jobsite-entrance", "radar-tiles");
};

export const removeRadar = (map: mapboxgl.Map, context: GlobalStoreContextType) => {
    if (map.getLayer("radar-tiles")) {
        map.removeLayer("radar-tiles");
    }
    if (map.getSource("radar-tiles")) {
        map.removeSource("radar-tiles");
    }

    map.flyTo({ center: map.getCenter(), zoom: context.currentProjectInfo.zoom || 16 });
};

export const openEditAreaMapHandler = (mappable: Area | Equipment, context: GlobalStoreContextType) => {
    const mapObject = mappable.getMapObject(context);
    if (!mapObject) {
        return;
    }
    context.setEditCurrentMappable(true);

    const map = context.mapRef?.current.getMap();
    if (!map) return;

    if (!map.hasControl(context.mapBoxDraw)) {
        map.addControl(context.mapBoxDraw, "top-left");
    }
    context.mapBoxDraw?.add(mapObject?.toFeature());
    if (mapObject?.geometry !== "point") {
        context.mapBoxDraw.changeMode("direct_select", { featureId: mapObject?.uuid });
    }
    map.flyTo({
        center: mapObject?.getCenter(),
        essential: true, // this animation is considered essential with respect to prefers-reduced-motion
    });
};

export const archiveMapObjectRemoveLayers = (
    context: GlobalStoreContextType,
    map: mapboxgl.Map,
    mapObject: MapObject
) => {
    mapObject.removeLayerFromMapbox(map);
    context.setNewMappableType(null);
};

export const addGeotiff = (map: mapboxgl.Map, geotiff: any) => {
    if (!map || !geotiff) {
        return;
    }

    if (map.getSource(`${geotiff.id}_geotiff`) || map.getLayer(`${geotiff.id}_geotiff`)) {
        return;
    }

    const geotiffUrl = `mapbox://nickdreiling.${geotiff.geotiff_name}`;
    map.addSource(`${geotiff.id}_geotiff`, {
        type: "raster",
        url: geotiffUrl,
    }).addLayer({
        id: `${geotiff.id}_geotiff`,
        type: "raster",
        source: `${geotiff.id}_geotiff`,
    });
};

export const setMapView = (map: mapboxgl.Map | null | undefined, projectInfo: any) => {
    if (!map) {
        return;
    }

    const center = map.getCenter();
    const zoom = map.getZoom();
    const style = map.getStyle().sprite?.includes("arcgis")
        ? globalAppConfig.ARC_GIS_IMAGERY_URL
        : map.getStyle().sprite;
    const newProjectInfo = projectInfo;
    newProjectInfo.lat_override = center.lat;
    newProjectInfo.lon_override = center.lng;
    newProjectInfo.map_zoom = zoom;
    newProjectInfo.map_style = style;

    PlotAPI.putProjectInfo(newProjectInfo.unique_token, newProjectInfo).then((response) => {
        if (response) {
            notification.success({
                message: "Default Map View Updated",
                description: "The map view has been saved to your jobsite successfully.",
            });
            return { success: true, message: "Default Map View Updated" };
        } else {
            notification.error({
                message: "Failed to Update Map View",
                description: "There was an issue saving the map view. Please try again.",
            });
            return { success: false, message: "Failed to Update Map View" };
        }
    });
};
export const updateMapIconSize = (map: mapboxgl.Map, iconSize: number, mapObject: MapObject) => {
    if (!map || !mapObject) {
        return;
    }
    let layerId = mapObject.uuid;

    if (mapObject.geometry == "polygon") {
        layerId = `${mapObject.uuid}_outline_icon`;
    }
    if (mapObject.geometry == "lineString") {
        layerId = `${mapObject.uuid}_line_icon}`;
    }

    if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, "icon-size", iconSize);
    }

    if (map.getLayer(`${mapObject.uuid}_lineCaps`)) {
        map.setLayoutProperty(`${mapObject.uuid}_lineCaps`, "icon-size", iconSize);
    }
};

export const onDrawUpdate = (e: mapboxgl.MapMouseEvent & { features: any[] }, context: GlobalStoreContextType) => {
    const newCurrentMapObject = Object.create(context.currentMapObject) as MapObject;

    newCurrentMapObject.coordinates = e.features[0].geometry.coordinates;
    context.setCurrentMapObject(newCurrentMapObject);
    const projectMapObjects = context.mapObjectsForProject(context.currentProjectInfo.unique_token).map((x) => {
        return Object.create(x) as MapObject;
    });
    if (projectMapObjects.find((x) => x.uuid === newCurrentMapObject.uuid)) {
        projectMapObjects.find((x) => x.uuid === newCurrentMapObject.uuid)!.coordinates =
            newCurrentMapObject.coordinates;
    } else {
        projectMapObjects.push(newCurrentMapObject);
    }
    context.setMapGeoJson(generateMapGeoJson(projectMapObjects));
};

export const onDrawCreate = (e: mapboxgl.MapMouseEvent & { features: any[] }, context: GlobalStoreContextType) => {
    const newMapObject = MapObject.fromFeature(e.features[0], context.currentProjectInfo, context.newMappableType);

    context.setCurrentMapObject(newMapObject);

    const newMappable =
        context.newMappableType == "equipment"
            ? Equipment.fromFeature(e.features[0], context.currentProjectInfo, context.currentProjectInfo.timezone)
            : Area.fromFeature(e.features[0], context.currentProjectInfo, context.currentProjectInfo.timezone);

    context.setCurrentMappable(newMappable);

    context.setEditCurrentMappable(true);
    context.setEditCurrentMappablePosition(true);

    const projectMapObjects = context.mapObjectsForProject(context.currentProjectInfo.unique_token);
    if (!projectMapObjects.find((x) => x.uuid === newMapObject.uuid)) {
        projectMapObjects.push(newMapObject);
    }
    context.setMapGeoJson(generateMapGeoJson(projectMapObjects));
    return newMapObject;
};

export const updateRadiusSize = (
    mapObject: MapObject,
    radius: MapObjectRadius,
    distance: number,
    units: "feet" | "meters" = "feet",
    index: number,
    context: GlobalStoreContextType
) => {
    if (!mapObject) {
        return;
    }
    const tempMapObject = Object.create(mapObject) as MapObject;
    const tempRadius = Object.create(radius) as MapObjectRadius;
    tempRadius.radius = distance;
    tempRadius.units = units;
    if (!tempMapObject.radiuses) {
        tempMapObject.radiuses = [tempRadius];
    } else {
        tempMapObject.radiuses[index] = tempRadius;
    }
    context.setCurrentMapObject(tempMapObject);
    const tempMapObjects = context.mapObjectsForProject(context.currentProjectInfo.unique_token);
    const mapObjectIndex = tempMapObjects.findIndex((x) => x.uuid === tempMapObject.uuid);
    tempMapObjects[mapObjectIndex] = tempMapObject;
    const tempGeoJson = generateMapGeoJson(tempMapObjects);
    context.setMapGeoJson(tempGeoJson);
};

export const randomColorGenerator = () => {
    return "#" + ((Math.random() * 0xffffff) << 0).toString(16).padStart(6, "0");
};

export const resetMapObject = (context: GlobalStoreContextType, mapObject: MapObject) => {
    const mappable = mapObject.getMappable(context);
    if (!mappable) {
        return;
    }
    const originalMapObject = mappable.getMapObject(context);
    if (!originalMapObject) {
        return;
    }
    const map = context.mapRef?.current?.getMap();
    if (!map) {
        return;
    }
    map.setPaintProperty(`${originalMapObject.uuid}`, "fill-color", originalMapObject.color || "#ff9e00");
};

export const mapObjectVisibility = (context: GlobalStoreContextType, mapObject: MapObject) => {
    if (mapObject.mappableId === -1) {
        // Newly created map objects that have not been saved yet
        return "visible";
    }
    const mappable = mapObject.getMappable(context);

    if (!mappable) {
        return "none";
    }
    if (context.activeSidebarKey == "deliveries") {
        const eventsForDay = context.deliveriesByProject
            .map((project) => project.deliveries)
            .flat()
            .filter((event) => (event?.startDateTime?.isSame(context.mapFilterDate, "day") ? true : false));
        return calendarShowArea(context, mappable, eventsForDay) ? "visible" : "none";
    } else if (context.mapFilterDate) {
        if (mappable instanceof Area) {
            const areaOpenStatus = areaOpen(
                mapObject,
                context.mapFilterDate,
                context.mapAreasForProject(context.currentProjectInfo.unique_token),
                context.equipmentForProject(context.currentProjectInfo.unique_token)
            );
            return areaOpenStatus ? "visible" : "none";
        } else if (mappable instanceof Equipment) {
            const equipmentOpenStatus = equipmentOpen(mappable, context.mapFilterDate);
            return equipmentOpenStatus ? "visible" : "none";
        }
    }
    return "visible";
};

export const loadMapLayers = (mapObjects: MapObject[], context: GlobalStoreContextType, layerId: string) => {
    return mapObjects.map((object, idx) => {
        if (!object || !context.mapRef?.current) {
            return null;
        }

        const mappable = object.getMappable(context);

        if (!mappable) {
            return null;
        }

        const visibility = Helper.mapObjectVisibility(context, object);

        switch (object.geometry) {
            case "point":
                return Helper.addIcon(object, layerId, context.mapRef?.current.getMap(), visibility);
            case "lineString":
                return Helper.addLine(object, layerId, visibility);
            case "polygon":
                return Helper.addPolygon(object, layerId, visibility);
            default:
                return null;
        }
    });
};
